import React, { useState } from "react";
import {
  Tag,
  Modal,
  Form,
  Steps,
  Select,
  InputNumber,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, Sparkles } from "lucide-react";
import axios from "axios";
import AiRecipe from "./AiRecipe";

const { Option } = Select;

const CATEGORIES = [
  { label: "GERD-Friendly", value: "GERD-Friendly" },
  { label: "IBS-Friendly", value: "IBS-Friendly" },
  { label: "Gastritis-Friendly", value: "Gastritis-Friendly" },
  { label: "Barrett's Esophagus", value: "Barretts-Esophagus-Friendly" },
];

const ETHNIC_STYLES = [
  "North-American",
  "South-American",
  "Chinese",
  "Italian",
  "Mexican",
  "Indian",
  "Japanese",
  "East-Asian",
  "SouthEast-Asian",
  "West-African",
  "East-African",
  "Central-African",
  "Middle-Eastern",
  "Caribbean",
  "Western-European",
  "Eastern-European",
  "Russian",
  "USA",
];

const FoodSearch = () => {
  const [text, setText] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const navigate = useNavigate();
  const [aiRecipe, setAiRecipe] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingOpenAiResponse, setLoadingOpenAiResponse] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [formData, setFormData] = useState({
    ingredients: [],
    condition: "",
    ethnicStyle: "",
    cookingTime: null,
  });

  const TIMEOUT_DURATION = 30000;

  const handleTagClick = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const searchRecipe = (e) => {
    if (e) e.preventDefault();
    const q = text.trim();
    if (!q && selectedTags.length === 0) {
      message.info("Enter a keyword or pick a category to search");
      return;
    }
    const params = new URLSearchParams();
    if (q) params.set("search_query", q);
    if (selectedTags.length > 0) params.set("category", selectedTags.join(","));
    params.set("calorieTotal", "0 TO 50000");
    navigate(`/search?${params.toString()}`);
  };

  const steps = [
    {
      title: "Ingredients",
      content: (
        <>
          <Form.Item
            label="Ingredients"
            name="ingredients"
            rules={[
              {
                required: true,
                message: "Please input at least one ingredient",
              },
            ]}
          >
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Enter ingredients and press enter or comma"
              value={formData.ingredients}
              onChange={(value) =>
                setFormData({ ...formData, ingredients: value })
              }
              tokenSeparators={[","]}
            />
          </Form.Item>
          <div style={{ marginTop: 8, color: "gray" }}>
            Separate ingredients with Enter or a comma.
          </div>
        </>
      ),
    },
    {
      title: "Condition",
      content: (
        <Form.Item
          label="Digestive condition"
          name="condition"
          rules={[{ required: true, message: "Please select a condition" }]}
        >
          <Select
            placeholder="Select a condition"
            value={formData.condition}
            onChange={(value) => setFormData({ ...formData, condition: value })}
          >
            {CATEGORIES.map((c) => (
              <Option key={c.value} value={c.value}>
                {c.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Style",
      content: (
        <Form.Item
          label="Cuisine style"
          name="ethnicStyle"
          rules={[{ required: true, message: "Please select a cuisine" }]}
        >
          <Select
            placeholder="Select a cuisine"
            value={formData.ethnicStyle}
            onChange={(value) =>
              setFormData({ ...formData, ethnicStyle: value })
            }
          >
            {ETHNIC_STYLES.map((style) => (
              <Option key={style} value={style}>
                {style}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Cooking Time",
      content: (
        <Form.Item
          label="Preferred cooking time (minutes)"
          name="cookingTime"
          rules={[{ required: true, message: "Please input cooking time" }]}
        >
          <InputNumber
            min={1}
            max={500}
            placeholder="Enter cooking time in minutes"
            value={formData.cookingTime}
            onChange={(value) =>
              setFormData({ ...formData, cookingTime: value })
            }
            style={{ width: "100%" }}
          />
        </Form.Item>
      ),
    },
  ];

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleSubmit = async () => {
    setLoadingOpenAiResponse(true);
    message.loading("Please don't click away. Processing your request.", 0);

    try {
      const prompt = `Please create a ${
        formData.condition
      } recipe using the following ingredients: ${formData.ingredients.join(
        ", "
      )}.
      The recipe should be of ${formData.ethnicStyle} style and should take ${
        formData.cookingTime
      } minutes to prepare/cook.
      Please format the response as follows:

  **TITLE: [Recipe Title]**

  **INGREDIENTS:**
      • [List each ingredient with quantity]

  **ESTIMATED TIME:**
      • Prep Time: [X] minutes
      • Cook Time: [Y] minutes
      • Total Time: [Z] minutes

**INSTRUCTIONS:**
      1. [Step-by-step instructions]

**NUTRITION INFORMATION:**
      • Calories:
      • Protein:
      • Carbs:
      • Fat:

**DIETARY NOTES:**
      [Any relevant dietary information or substitutions]
    [Notes]
    Ensure that the section headings are surrounded by double asterisks (e.g, **TITLE: ... ***)`;

      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(() => {
        source.cancel("Request timed out");
      }, TIMEOUT_DURATION);

      const response = await axios
        .post(
          "https://us-central1-gingerrmarket.cloudfunctions.net/generateChatResponse",
          { message: prompt },
          {
            cancelToken: source.token,
            headers: { "Content-Type": "application/json" },
            retry: 3,
            retryDelay: (retryCount) => retryCount * 1000,
          }
        )
        .finally(() => clearTimeout(timeoutId));

      if (!response.data) {
        message.error("Received empty response from server");
        throw new Error("Received empty response from server.");
      }

      setAiRecipe(response.data);
      message.destroy();
      message.success("Your recipe is ready!");
      setCurrentSection("ingredients");
    } catch (error) {
      if (error.response?.status === 429) {
        message.error("Rate limit reached. Please try again in a few minutes.");
      } else if (axios.isCancel(error)) {
        message.error("Request timed out. Please try again.");
      } else if (error.code === "ERR_NETWORK") {
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else if (error.response?.status >= 500) {
        message.error("Server error. Please try again later.");
      } else {
        message.error("An error occurred. Please try again.");
      }
    } finally {
      setLoadingOpenAiResponse(false);
      setIsModalVisible(false);
      message.destroy();
    }
  };

  return (
    <>
      <form onSubmit={searchRecipe} role="search" aria-label="Recipe search">
        <div className="flex items-stretch gap-2">
          <label htmlFor="hero-search" className="sr-only">
            Search digestive-friendly recipes
          </label>
          <div className="relative flex-1">
            <SearchIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
              size={18}
              aria-hidden
            />
            <input
              id="hero-search"
              type="search"
              placeholder="Search recipes, ingredients, or symptoms…"
              className="w-full h-14 pl-12 pr-4 rounded-full border border-stone-300 bg-white text-stone-900 text-base placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 focus:border-emerald-700 shadow-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="h-14 px-7 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors shadow-sm"
          >
            Search
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 justify-center">
          <span className="text-xs font-medium uppercase tracking-wider text-stone-500 mr-1">
            Filter
          </span>
          {CATEGORIES.map((tag) => (
            <Tag.CheckableTag
              key={tag.value}
              checked={selectedTags.includes(tag.value)}
              onChange={() => handleTagClick(tag.value)}
              className="!rounded-full !border !border-stone-300 !bg-white !text-stone-700 hover:!border-stone-500 !px-3 !py-1 !text-sm"
            >
              {tag.label}
            </Tag.CheckableTag>
          ))}
        </div>
      </form>

      <div className="mt-5 flex items-center justify-center gap-4 text-xs text-stone-500">
        <button
          type="button"
          onClick={() => setIsModalVisible(true)}
          className="inline-flex items-center gap-1.5 text-stone-700 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-500"
        >
          <Sparkles size={14} aria-hidden />
          Generate a personalised recipe with AI
        </button>
      </div>

      {aiRecipe && (
        <AiRecipe scrollToSection={currentSection} recipeText={aiRecipe} />
      )}

      <Modal
        title={
          <h3 className="text-xl font-serif text-stone-900">
            Craft a recipe with AI
          </h3>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form layout="vertical" className="p-2">
          <Steps
            current={currentStep}
            items={steps.map((s) => ({ title: s.title }))}
          />
          <div className="mt-8 space-y-4">
            {steps[currentStep].content}
            <div className="flex justify-end gap-3 mt-6">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={prev}
                  className="rounded-md border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                >
                  Previous
                </button>
              )}
              {currentStep < steps.length - 1 && (
                <button
                  type="button"
                  onClick={next}
                  disabled={
                    currentStep === 0 && formData.ingredients.length === 0
                  }
                  className="rounded-md bg-stone-900 text-white px-4 py-2 text-sm hover:bg-stone-800 disabled:bg-stone-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              )}
              {currentStep === steps.length - 1 && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loadingOpenAiResponse}
                  className="rounded-md bg-emerald-800 text-white px-4 py-2 text-sm hover:bg-emerald-900 disabled:bg-stone-400"
                >
                  {loadingOpenAiResponse ? "Generating…" : "Generate recipe"}
                </button>
              )}
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default FoodSearch;
